/**
 * Кто и когда видит поп-ап «что нового» (#946).
 *
 * Гейт, а не сам диалог: решение «показывать ли» отделено от отрисовки, потому
 * что контексты разные — в вебе состояние приходит вместе с `/users/me`, во
 * встроенном приложении своим запросом, — а правило показа обязано быть одним
 * и тем же.
 *
 * Отметка пишется при ЗАКРЫТИИ, а закрыть можно только одним способом
 * (`WhatsNewDialog` сводит крестик, Esc и кнопку в один обработчик): иначе
 * человек, нажавший Esc, получил бы тот же поп-ап при следующем входе.
 *
 * Провал записи гасится намеренно: худшее последствие — поп-ап покажется ещё
 * раз, и это несравнимо лучше, чем сообщение об ошибке поверх новости о
 * релизе.
 */
import { useCallback, useState } from 'react';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { embeddedTabTarget } from '@/routes/embedded/dashboard/types';
import type { ProductPackageKey } from '@aica/core';
import { logError } from '@/lib/logger';
import { WHATS_NEW, shouldShowWhatsNew } from './release';
import { WhatsNewDialog } from './WhatsNewDialog';

export interface WhatsNewGateProps {
  lastSeenRelease: string | null | undefined;
  userCreatedAt: string | null | undefined;
  /** Записать отметку. Разная в вебе и в embedded — отсюда параметром. */
  onSeen: (version: string) => Promise<unknown>;
  /**
   * false, когда записать отметку некуда — embedded-сессия без связанного
   * пользователя. Показывать поп-ап тогда нельзя: он вернулся бы при каждом
   * заходе.
   */
  canRecord?: boolean;
  /**
   * Тариф организации — ОБЯЗАТЕЛЕН, и оба источника серверные: веб берёт его
   * из ростера `/users/me`, встроенное приложение — из `/embedded/whats-new`.
   *
   * `TierContext` здесь не годится дважды. Во встроенной оболочке его провайдер
   * не смонтирован, а в вебе он запрашивает подписку только при `billing.read`
   * — для admin, agent и supervisor не запрашивает никогда и отдаёт
   * нейтральный `free`. Опираться на него значило либо сказать Pro-организации
   * «доступно на Pro и выше», либо (с проверкой `hasPlanData`) промолчать
   * Free-организации о том, чего у неё нет. Оба исхода — в поп-апе, который
   * показывается один раз (#978).
   *
   * `null` — тариф действительно неизвестен (организация не выбрана или воркер
   * ещё без этого поля). Тогда пометки нет: лучше промолчать, чем соврать.
   */
  tier: ProductPackageKey | null;
  /** Кнопка тарифов — только тем, кому биллинг открыт. См. `WhatsNewDialog`. */
  canViewPlans: boolean;
}

export function WhatsNewGate({
  lastSeenRelease,
  userCreatedAt,
  onSeen,
  canRecord = true,
  tier,
  canViewPlans,
}: WhatsNewGateProps) {
  const { appNavigate } = useAppNavigate();
  // Закрыто в этой сессии — чтобы диалог исчез сразу, не дожидаясь, пока
  // запись отметки долетит до сервера и обновит состояние.
  const [dismissed, setDismissed] = useState(false);

  const release = WHATS_NEW;

  const handleClose = useCallback(() => {
    setDismissed(true);
    if (!release) return;
    void Promise.resolve(onSeen(release.version)).catch((error) => {
      logError('Failed to record seen release', {
        component: 'WhatsNewGate',
        error: error instanceof Error ? error.message : String(error),
      });
    });
  }, [onSeen, release]);

  const handleNavigate = useCallback(
    (to: string) => {
      // Переход по пункту — то же закрытие: человек ушёл смотреть на саму
      // возможность, и встречать его тем же поп-апом незачем. Отметка и
      // логирование её провала живут в одном месте, а не в двух похожих.
      handleClose();
      // Весь переход — через `appNavigate`: во встроенной оболочке он ведёт на
      // вкладку-двойник, а маршрут без двойника открывает отдельным окном,
      // сохраняя параметры в адресе. Прямой вызов роутера вынес бы iframe из
      // его cookie-сессии вместе с контекстом портала.
      appNavigate(to, { embeddedTo: embeddedTabTarget(to) });
    },
    [appNavigate, handleClose]
  );

  if (dismissed || !canRecord || !release) return null;
  // Проверка `release` выше — не дубль условия внутри `shouldShowWhatsNew`, а
  // сужение типа: без неё диалогу пришлось бы верить утверждению `release!`.
  if (!shouldShowWhatsNew({ release, lastSeenRelease, userCreatedAt })) return null;

  return (
    <WhatsNewDialog
      release={release}
      tier={tier}
      canViewPlans={canViewPlans}
      onClose={handleClose}
      onNavigate={handleNavigate}
    />
  );
}
